import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../button';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { Marginer } from '../marginer';
import { useMutation } from '@apollo/client';
import { CONFIRM_SIGN_UP } from '../../services/authService/queries';
import { toast } from 'react-toastify';
import { Modal } from '../modal';
import { modalActions } from '../../../store/authStore/modalStore';
import { useSnapshot } from 'valtio';
import { modalStore } from '../../../store/authStore/modalStore';
import { parseAuthEmail, parseVerifyCode } from '../../constants/authRoutes';
import { Colors } from '../../styles/colors';
import { AUTH_FORM_COPY, isValidEmailAddress } from '../../utils/authFormValidation';

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

const VerifyCodeContainer = styled.div`
  ${tw`flex flex-col items-center justify-center w-full`}
`;

const Container = styled.div`
  ${tw`flex flex-col gap-4 max-w-md w-full`}
`;

const Title = styled.h2`
  ${tw`text-xl md:text-2xl font-bold mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-text-secondary text-sm mb-4`}
`;

const Input = styled.input<{ $invalid?: boolean }>`
  ${tw`border border-solid border-border-default rounded-md p-2 w-full box-border`}
  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: ${Colors.Error};
    `}
`;

const FieldError = styled.p`
  ${tw`text-sm mt-1 mb-0`}
  color: ${Colors.Error};
`;

const Label = styled.label`
  ${tw`text-sm text-text-secondary`}
`;

interface VerifyCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VerifyCodeModal = ({ isOpen, onClose }: VerifyCodeModalProps) => {
  const { search } = useLocation();
  const { currentEmail } = useSnapshot(modalStore);
  const decodedSearch = decodeURIComponent(search);
  const params = queryString.parse(decodedSearch, {
    parseNumbers: true,
    parseBooleans: true,
  });

  const emailFromUrl = parseAuthEmail(params.email) || currentEmail || '';
  const codeFromUrl = parseVerifyCode(params.verifyCode, params.code);

  const [verifyCode, { loading: verifyLoading }] = useMutation(CONFIRM_SIGN_UP);

  const handleVerifyCode = async (currentEmail: string, currentCode: string) => {
    if (!String(currentCode).trim()) {
      toast.error('Please enter verification code');
      return;
    }

    if (!isValidEmailAddress(String(currentEmail))) {
      toast.error(AUTH_FORM_COPY.invalidEmail);
      return;
    }

    try {
      const { data, errors } = await verifyCode({
        variables: {
          input: {
            email: String(currentEmail).trim(),
            code: String(currentCode).trim(),
          },
        },
      });

      if (errors?.length || !data?.confirmSignUp?.success) {
        const message =
          data?.confirmSignUp?.message ||
          errors?.[0]?.message ||
          'Invalid verification code. Please try again.';
        toast.error(message);
        return;
      }

      toast.success(data.confirmSignUp.message || 'Email verified successfully!');
      modalActions.switchToSignIn(String(currentEmail).trim());
    } catch (error: any) {
      toast.error(error?.message || 'Invalid verification code. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentAlign="center">
      <VerifyCodeContainer>
        <VerifyCodeForm
          email={emailFromUrl}
          code={codeFromUrl}
          handleVerifyCode={handleVerifyCode}
          isLoading={verifyLoading}
        />
      </VerifyCodeContainer>
    </Modal>
  );
};

const VerifyCodeForm = ({
  email,
  code,
  handleVerifyCode,
  isLoading,
}: {
  email: string;
  code: string;
  handleVerifyCode: (email: string, code: string) => void;
  isLoading: boolean;
}) => {
  const [codeState, setCodeState] = useState(code || '');
  const [emailState, setEmailState] = useState(email);
  const [emailError, setEmailError] = useState<string | undefined>();

  useEffect(() => {
    setEmailState(email);
  }, [email]);

  useEffect(() => {
    if (code) {
      setCodeState(code);
    }
  }, [code]);

  return (
    <Container>
      <div>
        <Title>Verify your email</Title>
        <Subtitle>
          We sent a verification code to {emailState || 'your email'}. Enter the code below to
          verify your email address.
        </Subtitle>
      </div>

      <FormGroup>
        <Label>Email</Label>
        <Input
          type="email"
          value={emailState}
          onChange={e => {
            setEmailState(e.target.value);
            setEmailError(undefined);
          }}
          placeholder="Enter your email"
          autoComplete="email"
          $invalid={!!emailError}
          aria-invalid={!!emailError}
        />
        {emailError && <FieldError>{emailError}</FieldError>}
      </FormGroup>
      <FormGroup>
        <Label>Verification Code</Label>
        <Input
          type="text"
          value={codeState}
          onChange={e => setCodeState(e.target.value)}
          placeholder="Enter verification code"
          autoComplete="one-time-code"
          inputMode="numeric"
        />
      </FormGroup>

      <Marginer direction="vertical" margin="1em" />

      <Button
        text="Verify Email"
        onClick={() => {
          if (!isValidEmailAddress(emailState)) {
            setEmailError(AUTH_FORM_COPY.invalidEmail);
          }
          handleVerifyCode(emailState, codeState);
        }}
        buttonWidth="100%"
        isLoading={isLoading}
        isDisabled={isLoading}
      />
    </Container>
  );
};

export default VerifyCodeModal;
